import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import type { StarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { buildDmEmailHref } from '#src/app/shared/utils/decisionMaker';

type DecisionMaker = StarterDashboardV2PageData['petition']['decisionMakersConnection']['nodes'][number];

type DecisionMakerWithEmailHref = DecisionMaker & { emailHref: string };

type UseDmCardsReturn = ModelHookResult<
	{
		slug: string;
		signatureCount: number;
		decisionMakers: readonly DecisionMakerWithEmailHref[];
		allDmResponses: ReadonlyArray<{ id: string; decisionMaker: { id: string } }>;
	},
	{
		trackProfileLinkClick: (decisionMakerId: string) => void;
		trackEmailDmClick: (decisionMakerId: string) => void;
		trackReadResponseClick: (responseId: string) => void;
	}
>;

export function useDmCards(): UseDmCardsReturn {
	const track = useTracking();
	const { translate } = useI18n();

	const {
		data: {
			petition: {
				ask,
				slug,
				user: { displayName: starterDisplayName },
				signatureState: {
					signatureCount: { total: signatureCount },
				},
				decisionMakersConnection: { nodes: decisionMakers },
				dmResponsesConnection: { nodes: allDmResponses },
			},
		},
	} = useStarterDashboardV2PageData();

	const decisionMakersWithEmailHref = decisionMakers.map((dm) => {
		if (!dm.email) {
			return { ...dm, emailHref: 'mailto:' };
		}

		return {
			...dm,
			emailHref: buildDmEmailHref(
				{ email: dm.email, displayName: dm.displayName },
				{ ask, slug, signatureCount: signatureCount.toString(), starterDisplayName },
				translate,
			),
		};
	});

	const trackProfileLinkClick = useCallback(
		(decisionMakerId: string) => {
			void track('decision_maker_profile_link_click', {
				decision_maker_id: decisionMakerId,
				source_location: 'starter_dashboard',
			});
		},
		[track],
	);

	const trackEmailDmClick = useCallback(
		(decisionMakerId: string) => {
			void track('starter_dashboard_dm_list_email_click', { decision_maker_id: decisionMakerId });
		},
		[track],
	);

	const trackReadResponseClick = useCallback(
		(responseId: string) => {
			void track('starter_dashboard_read_dm_response_click', { response_id: responseId });
		},
		[track],
	);

	return {
		data: {
			slug,
			signatureCount,
			decisionMakers: decisionMakersWithEmailHref,
			allDmResponses,
		},
		actions: {
			trackProfileLinkClick,
			trackEmailDmClick,
			trackReadResponseClick,
		},
	};
}
